import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';

/**
 * Audio Context for managing global audio state using TikTok's approach
 * 
 * TikTok's Strategy:
 * 1. Capture the first user interaction to unlock Web Audio API
 * 2. Create a silent audio context and play a silent sound
 * 3. This unlocks autoplay with sound for all subsequent videos
 * 4. Use a global audio context that persists across video changes
 * 
 * To disable this feature:
 * 1. In Azure Portal, go to your App Service
 * 2. Navigate to Configuration > Application settings
 * 3. Add or modify the setting REACT_APP_ENABLE_AUTO_UNMUTE to "false"
 * 4. Save the changes and restart the app service
 * 
 * Or locally:
 * 1. Add REACT_APP_ENABLE_AUTO_UNMUTE=false to your .env file
 * 2. Restart the development server
 */

// Create a context for managing audio state
const AudioContext = createContext();

// Check if auto-unmute feature is enabled (default to true if not specified)
const isAutoUnmuteEnabled = () => {
  return process.env.REACT_APP_ENABLE_AUTO_UNMUTE !== 'false';
};

// Provider component that wraps the app and makes audio state available
export const AudioProvider = ({ children }) => {
  // Flag: skip all audio work on agency/admin routes (they don't use video feed)
  const isAgencyRoute = window.location.pathname.startsWith('/agency') || window.location.pathname.startsWith('/vusub-internal');

  // Check if the feature is enabled
  const autoUnmuteEnabled = isAutoUnmuteEnabled() && !isAgencyRoute;
  
  // State to track if audio should be unmuted - default to true for sound on by default
  const [audioEnabled, setAudioEnabled] = useState(true);
  
  // State to track if Web Audio API has been unlocked
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  
  // State to track if we should show the "tap to enable sound" overlay
  const [showSoundPrompt, setShowSoundPrompt] = useState(false);
  
  // Ref to store the Web Audio API context
  const audioContextRef = useRef(null);
  const silentAudioRef = useRef(null);
  const unlockingRef = useRef(false);
  
  // Enhanced TikTok-style Web Audio API unlock strategy - Improved for mobile browsers
  const unlockAudioContext = useCallback(async () => {
    if (audioUnlocked || !autoUnmuteEnabled || unlockingRef.current) return;
    unlockingRef.current = true;
    
    try {
      
      // Enhanced mobile browser detection
      const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                       ('ontouchstart' in window) || 
                       (navigator.maxTouchPoints > 0);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isAndroid = /Android/i.test(navigator.userAgent);
      
      // Method 1: Enhanced Web Audio API unlock with mobile optimizations
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        if (!audioContextRef.current) {
          // Mobile-specific AudioContext configuration
          const contextOptions = {};
          if (isMobile) {
            // Mobile browsers benefit from specific sample rates
            contextOptions.sampleRate = isIOS ? 44100 : 48000;
          }
          
          audioContextRef.current = new AudioContextClass(contextOptions);
        }
        
        const audioContext = audioContextRef.current;
        
        // Resume the audio context if it's suspended
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        // Create and play a silent buffer with mobile optimizations
        const sampleRate = audioContext.sampleRate;
        const bufferLength = Math.max(1, Math.floor(sampleRate * 0.01)); // 10ms buffer minimum
        const buffer = audioContext.createBuffer(1, bufferLength, sampleRate);
        
        // Fill with very quiet noise instead of complete silence (helps with some mobile browsers)
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < bufferLength; i++) {
          channelData[i] = (Math.random() - 0.5) * 0.0001; // Very quiet noise
        }
        
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        
        // Mobile-specific audio routing
        if (isMobile) {
          // Create a gain node for better mobile compatibility
          const gainNode = audioContext.createGain();
          gainNode.gain.setValueAtTime(0.001, audioContext.currentTime); // Very quiet
          source.connect(gainNode);
          gainNode.connect(audioContext.destination);
        } else {
          source.connect(audioContext.destination);
        }
        
        source.start(0);
      }
      
      // Method 2: Enhanced HTML5 Audio unlock (TikTok's primary method) with mobile optimizations
      if (!silentAudioRef.current) {
        silentAudioRef.current = new Audio();
        
        // Mobile-specific audio configuration
        if (isMobile) {
          // iOS-specific optimizations
          if (isIOS) {
            // Use a shorter, iOS-optimized silent audio
            silentAudioRef.current.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
            silentAudioRef.current.volume = 0.005; // Even quieter for iOS
          } else if (isAndroid) {
            // Android-optimized silent audio
            silentAudioRef.current.src = 'data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAATNTYW1zdW5nIEVsZWN0cm9uaWNzIENvLixMdGQuAFRQRTEAAAAVAAABM1NhbXN1bmcgRWxlY3Ryb25pY3MAVFNTRQAAAA8AAAEzTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//OEAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAAEAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV6urq6urq6urq6urq6urq6urq6urq6urq6v////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAASDs90hvAAAAAAAAAAAAAAAAAAAA//MUZAAAAAGkAAAAAAAAA0gAAAAATEFN//MUZAMAAAGkAAAAAAAAA0gAAAAARTMu//MUZAYAAAGkAAAAAAAAA0gAAAAAOTku//MUZAkAAAGkAAAAAAAAA0gAAAAANVVV';
            silentAudioRef.current.volume = 0.01; // Slightly higher for Android
          }
        } else {
          // Desktop browser - use standard silent audio
          silentAudioRef.current.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAAAQESsAEBErAAAAAAAAgAQAGRhdGEEAAAAAAAA';
          silentAudioRef.current.volume = 0.01;
        }
        
        silentAudioRef.current.preload = 'auto';
        silentAudioRef.current.loop = false;
        
        // Mobile-specific audio element configuration
        if (isMobile) {
          silentAudioRef.current.setAttribute('playsinline', '');
          silentAudioRef.current.setAttribute('webkit-playsinline', '');
          
          // iOS-specific attributes
          if (isIOS) {
            silentAudioRef.current.setAttribute('controls', 'false');
            silentAudioRef.current.muted = false; // Ensure it's not muted for iOS
          }
        }
      }
      
      // TikTok's approach: Play the silent audio immediately with mobile optimizations
      const playPromise = silentAudioRef.current.play();
      if (playPromise !== undefined) {
        await playPromise.catch(e => {
          
          // Mobile-specific retry logic
          if (isMobile && e.name === 'NotAllowedError') {
          }
        });
      }
      
      // Method 3: Create multiple audio elements for better compatibility (enhanced for mobile)
      const unlockPromises = [];
      
      // Create multiple test audio elements with different formats for maximum compatibility
      const audioFormats = [
        'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAAAQESsAEBErAAAAAAAAgAQAGRhdGEEAAAAAAAA',
        'data:audio/mpeg;base64,//MUxAAAAANIAAAAAExBTUUzLjk4LjIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
        'data:audio/ogg;base64,T2dnUwACAAAAAAAAAADqnjMlAAAAAOyyzPIBHgF2b3JiaXMAAAAAAUAfAABAHwAAQB8AAEAfAACZAU9nZ1MAAAAAAAAAAAAA6p4zJQEAAAANJGeKCj3//////////5ADdm9yYmlzLQAAAQV2b3JiaXMrQkNWAQAIAAAAMUwgTGF2ZjU4Ljc2LjEwMAEAAAAYAAAAZW5jb2Rlcj1MYXZmNTguNzYuMTAwAQV2b3JiaXMtQkNWAQAAAQAAABhYHgtH'
      ];
      
      audioFormats.forEach((src, index) => {
        const testAudio = new Audio();
        testAudio.src = src;
        testAudio.volume = 0;
        
        if (isMobile) {
          testAudio.setAttribute('playsinline', '');
          testAudio.setAttribute('webkit-playsinline', '');
        }
        
        const testPromise = testAudio.play().catch(() => {
        });
        
        unlockPromises.push(testPromise);
      });
      
      // Wait for all unlock attempts to complete
      await Promise.allSettled(unlockPromises);
      
      // Method 4: Mobile-specific additional unlock techniques
      if (isMobile) {
        
        // iOS-specific unlock
        if (isIOS) {
          try {
            // Create a very short audio context test for iOS
            const iosAudioContext = new (window.AudioContext || window.webkitAudioContext)();
            const iosOscillator = iosAudioContext.createOscillator();
            const iosGain = iosAudioContext.createGain();
            
            iosOscillator.connect(iosGain);
            iosGain.connect(iosAudioContext.destination);
            iosGain.gain.setValueAtTime(0, iosAudioContext.currentTime);
            iosOscillator.frequency.setValueAtTime(440, iosAudioContext.currentTime);
            iosOscillator.start(iosAudioContext.currentTime);
            iosOscillator.stop(iosAudioContext.currentTime + 0.01);
            
            
            // Clean up
            setTimeout(() => {
              iosAudioContext.close();
            }, 100);
          } catch (iosError) {
          }
        }
        
        // Android-specific unlock
        if (isAndroid) {
          try {
            // Android Chrome benefits from multiple concurrent audio elements
            const androidAudios = [];
            for (let i = 0; i < 3; i++) {
              const androidAudio = new Audio();
              androidAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAAAQESsAEBErAAAAAAAAgAQAGRhdGEEAAAAAAAA';
              androidAudio.volume = 0;
              androidAudio.setAttribute('playsinline', '');
              androidAudios.push(androidAudio);
              
              androidAudio.play().catch(() => {
              });
            }
            
            
            // Clean up after a short delay
            setTimeout(() => {
              androidAudios.forEach(audio => {
                audio.pause();
                audio.src = '';
              });
            }, 500);
          } catch (androidError) {
          }
        }
      }
      
      setAudioUnlocked(true);
      
    } catch (error) {
      console.warn('⚠️ Audio unlock failed:', error.message);
      // Still set to unlocked to prevent infinite retries
      setAudioUnlocked(true);
    } finally {
      unlockingRef.current = false;
    }
  }, [audioUnlocked, autoUnmuteEnabled]);
  
  // Function to enable audio (to be called after user interaction)
  const enableAudio = async () => {
    // Only enable audio if the feature is enabled
    if (autoUnmuteEnabled) {
      setAudioEnabled(true);
      
      // Unlock audio context on first interaction
      if (!audioUnlocked) {
        await unlockAudioContext();
      }
    } else {
      // audio disabled — no log needed
    }
  };
  
  // Function to disable audio
  const disableAudio = () => {
    setAudioEnabled(false);
  };
  
  // Modern TikTok approach: No sound prompts, just seamless audio unlocking
  const triggerSoundPrompt = useCallback(() => {
    return () => {};
  }, []);
  
  // TikTok's interaction capture strategy - always listen for interactions but be smarter about prompts
  useEffect(() => {
    if (!autoUnmuteEnabled || audioUnlocked) return;
    
    const handleUserInteraction = async (event) => {
      setShowSoundPrompt(false);
      await unlockAudioContext();
    };
    
    // Only listen to qualifying user gestures — Chrome does NOT grant AudioContext
    // permission for scroll or focus events, so including them floods the console
    // with "AudioContext was not allowed to start" warnings on every scroll tick.
    document.addEventListener('click', handleUserInteraction, true);
    document.addEventListener('touchstart', handleUserInteraction, true);
    document.addEventListener('touchend', handleUserInteraction, true);
    document.addEventListener('keydown', handleUserInteraction, true);
    document.addEventListener('pointerdown', handleUserInteraction, true);
    
    return () => {
      document.removeEventListener('click', handleUserInteraction, true);
      document.removeEventListener('touchstart', handleUserInteraction, true);
      document.removeEventListener('touchend', handleUserInteraction, true);
      document.removeEventListener('keydown', handleUserInteraction, true);
      document.removeEventListener('pointerdown', handleUserInteraction, true);
    };
  }, [autoUnmuteEnabled, audioUnlocked, unlockAudioContext]);
  
  // Remove event listeners when audio is unlocked
  useEffect(() => {
    // cleanup happens automatically when the interaction useEffect re-runs
  }, [audioUnlocked]);
  
  // Log state changes only in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
    }
  }, [audioEnabled, audioUnlocked]);
  
  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (silentAudioRef.current) {
        silentAudioRef.current.pause();
        silentAudioRef.current = null;
      }
    };
  }, []);
  
  // The value that will be available to consumers of this context
  const value = {
    audioEnabled,
    audioUnlocked,
    enableAudio,
    disableAudio,
    autoUnmuteEnabled,
    unlockAudioContext,
    showSoundPrompt,
    setShowSoundPrompt,
    triggerSoundPrompt
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

// Custom hook for components to easily access the audio context
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export default AudioContext;
